import React, { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, ExternalLink } from "lucide-react";

declare global {
  interface Window {
    EBWidgets?: {
      createWidget: (options: {
        widgetType: string;
        eventId: string;
        iframeContainerId?: string;
        iframeContainerHeight?: number;
        modal?: boolean;
        modalTriggerElementId?: string;
        onOrderComplete?: () => void;
      }) => void;
    };
  }
}

interface EventbriteEmbedProps {
  mode: "modal" | "inline";
  buttonText?: string;
  buttonClassName?: string;
  iframeHeight?: number;
  eventId?: string;
  onOrderComplete?: () => void;
}

const DEFAULT_EVENT_ID = "1977063332649";

const EventbriteEmbed: React.FC<EventbriteEmbedProps> = ({
  mode,
  buttonText = "Register on Eventbrite",
  buttonClassName = "",
  iframeHeight = 425,
  eventId,
  onOrderComplete,
}) => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(false);
  const [widgetRendering, setWidgetRendering] = useState(true);
  const widgetCreated = useRef(false);
  const containerId = useRef(`eventbrite-widget-${Math.random().toString(36).substr(2, 9)}`);
  const triggerId = useRef(`eventbrite-trigger-${Math.random().toString(36).substr(2, 9)}`);

  const finalEventId = eventId || DEFAULT_EVENT_ID;

  // Load Eventbrite script
  useEffect(() => {
    const existingScript = document.querySelector('script[src="https://www.eventbrite.com/static/widgets/eb_widgets.js"]');
    
    if (existingScript) {
      if (window.EBWidgets) {
        setScriptLoaded(true);
      } else {
        existingScript.addEventListener("load", () => setScriptLoaded(true));
        existingScript.addEventListener("error", () => setScriptError(true));
      }
      return;
    }

    const script = document.createElement("script");
    script.src = "https://www.eventbrite.com/static/widgets/eb_widgets.js";
    script.async = true;
    script.onload = () => setScriptLoaded(true);
    script.onerror = () => setScriptError(true);
    document.body.appendChild(script);

    return () => {
      // Don't remove the script as other instances might use it
    };
  }, []);

  // Create widget after script loads
  useEffect(() => {
    if (!scriptLoaded || !window.EBWidgets || widgetCreated.current) return;

    try {
      if (mode === "inline") {
        window.EBWidgets.createWidget({
          widgetType: "checkout",
          eventId: finalEventId,
          iframeContainerId: containerId.current,
          iframeContainerHeight: iframeHeight,
          onOrderComplete: () => {
            console.log("Eventbrite order complete");
            onOrderComplete?.();
          },
        });
      } else {
        // Modal mode
        window.EBWidgets.createWidget({
          widgetType: "checkout",
          eventId: finalEventId,
          modal: true,
          modalTriggerElementId: triggerId.current,
          onOrderComplete: () => {
            console.log("Eventbrite order complete");
            onOrderComplete?.();
          },
        });
      }
      widgetCreated.current = true;
      
      // Give the widget iframe time to render
      const timer = setTimeout(() => setWidgetRendering(false), 1500);
      return () => clearTimeout(timer);
    } catch (error) {
      console.error("Error creating Eventbrite widget:", error);
      setScriptError(true);
    }
  }, [scriptLoaded, mode, finalEventId, iframeHeight]);

  if (scriptError) {
    return (
      <div className="text-center p-6 bg-muted/50 rounded-lg border border-border">
        <p className="text-muted-foreground mb-4">
          Unable to load registration widget.
        </p>
        <Button asChild variant="outline">
          <a
            href={`https://www.eventbrite.com/e/${finalEventId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="gap-2"
          >
            Register on Eventbrite
            <ExternalLink className="w-4 h-4" />
          </a>
        </Button>
      </div>
    );
  }

  if (mode === "inline") {
    const isLoading = !scriptLoaded || widgetRendering;
    return (
      <div className="w-full">
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">
              Loading registration form...
            </p>
          </div>
        )}
        <div 
          id={containerId.current} 
          className={isLoading ? "hidden" : ""}
        />
      </div>
    );
  }

  // Modal mode
  return (
    <div>
      {!scriptLoaded ? (
        <Button disabled className={buttonClassName}>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Loading...
        </Button>
      ) : (
        <Button
          id={triggerId.current}
          className={buttonClassName}
        >
          {buttonText}
        </Button>
      )}
    </div>
  );
};

export default EventbriteEmbed;
